import { useState, useRef, useEffect } from 'react';
import {
  FileText,
  Star,
  FolderOpen,
  Cloud,
  CloudOff,
  Loader2,
  Share2,
  MoreVertical,
  MessageSquare,
  History,
  ChevronDown,
  ArrowLeft,
  Download,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';
import './styles.css';

/**
 * EditorHeader - Google Docs-like header with document info, save status, and share
 */
export function EditorHeader({
  filename,
  onFilenameChange,
  isSaving,
  hasUnsavedChanges,
  lastSavedAt,
  onShare,
  onShowComments,
  onShowHistory,
  onToggleAssistant,
  onDownload,
  collaborators = [],
  isStarred = false,
  onToggleStar,
  onClose,
}) {
  const { t } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(filename);
  const titleInputRef = useRef(null);

  useEffect(() => {
    setEditedTitle(filename);
  }, [filename]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (editedTitle.trim() && editedTitle !== filename) {
      onFilenameChange?.(editedTitle.trim());
    } else {
      setEditedTitle(filename);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleBlur();
    } else if (e.key === 'Escape') {
      setEditedTitle(filename);
      setIsEditingTitle(false);
    }
  };

  const formatLastSaved = (date) => {
    if (!date) return '';
    const now = new Date();
    const saved = new Date(date);
    const diffMs = now - saved;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return t('editor.justNow', 'Just now');
    if (diffMins < 60) return t('editor.minsAgo', '{{mins}} min ago', { mins: diffMins });

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return t('editor.hoursAgo', '{{hours}} hours ago', { hours: diffHours });

    return saved.toLocaleDateString('ru-RU');
  };

  const getSaveStatus = () => {
    if (isSaving) {
      return (
        <span className="save-status saving">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('editor.saving', 'Saving...')}
        </span>
      );
    }
    if (hasUnsavedChanges) {
      return (
        <span className="save-status unsaved">
          <CloudOff className="w-3 h-3" />
          {t('editor.unsavedChanges', 'Unsaved changes')}
        </span>
      );
    }
    return (
      <span className="save-status saved">
        <Cloud className="w-3 h-3" />
        {lastSavedAt ? formatLastSaved(lastSavedAt) : t('editor.saved', 'Saved')}
      </span>
    );
  };

  return (
    <div className="editor-header">
      <div className="editor-header-left">
        {/* Back Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="header-square-btn mr-3"
                onClick={onClose}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('editor.back', 'Back')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Document Icon */}
        <div className="doc-icon">
          <FileText className="w-6 h-6 text-blue-600" />
        </div>

        {/* Title and Status */}
        <div className="title-section">
          <div className="title-row">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                className="title-input"
              />
            ) : (
              <TruncatedTooltip content={filename || t('editor.untitled', 'Untitled document')} className="max-w-[400px]">
                <h1 className="doc-title truncate" onClick={handleTitleClick}>
                  {filename || t('editor.untitled', 'Untitled document')}
                </h1>
              </TruncatedTooltip>
            )}

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className={`star-btn ${isStarred ? 'starred' : ''}`}
                    onClick={onToggleStar}
                  >
                    <Star className="w-4 h-4" fill={isStarred ? 'currentColor' : 'none'} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {isStarred ? t('editor.unstar', 'Remove from starred') : t('editor.star', 'Add to starred')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="folder-btn">
                    <FolderOpen className="w-4 h-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('editor.moveToFolder', 'Move to folder')}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="status-row">
            {getSaveStatus()}
          </div>
        </div>
      </div>

      <div className="editor-header-right">
        {/* Assistant: shows or hides the editor's AI dock (edit sessions with the aip panel) */}
        {onToggleAssistant && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="header-icon-btn" onClick={onToggleAssistant} aria-label={t('editor.assistant', 'Assistant')}>
                  <Sparkles className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{t('editor.assistant', 'Assistant')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {/* History Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="header-icon-btn" onClick={onShowHistory}>
                <History className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('editor.versionHistory', 'Version history')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Comments Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="header-icon-btn" onClick={onShowComments}>
                <MessageSquare className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('editor.comments', 'Comments')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Collaborator Avatars */}
        {collaborators.length > 0 && (
          <div className="collaborators">
            {collaborators.slice(0, 3).map((user, idx) => (
              <TooltipProvider key={user.uuid || idx}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="collaborator-avatar"
                      style={{
                        backgroundColor: user.color || `hsl(${(idx * 137) % 360}, 70%, 50%)`,
                        zIndex: 10 - idx,
                      }}
                    >
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} />
                      ) : (
                        <span>{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{user.name || t('editor.anonymous', 'Anonymous')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            {collaborators.length > 3 && (
              <div className="collaborator-avatar more">
                +{collaborators.length - 3}
              </div>
            )}
          </div>
        )}

        {/* Share Button — only for those who may share (owner, manager) */}
        {onShare && (
          <Button
            onClick={onShare}
            className="share-btn text-white hover:text-white"
            size="sm"
          >
            <Share2 className="w-4 h-4 mr-2" />
            {t('editor.share', 'Share')}
          </Button>
        )}

        {/* More Options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="header-icon-btn">
              <MoreVertical className="w-5 h-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onShowHistory}>
              <History className="w-4 h-4 mr-2" />
              {t('editor.versionHistory', 'Version history')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onShowComments}>
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('editor.comments', 'Comments')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDownload}>
              <Download className="w-4 h-4 mr-2" />
              {t('editor.download', 'Download')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onClose} className="text-red-600">
              {t('editor.close', 'Close')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
